import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Calendar, Clock, MessageSquare, User } from 'lucide-react'
import api from '../services/api'
import { API_ENDPOINTS } from '../utils/constants'

const BookOneOnOneModal = ({
  isOpen,
  onClose,
  onSuccess,
  facilitators = [],
  programSlug = 'build3-cohort-7',
}) => {
  const [formData, setFormData] = useState({
    facilitatorName: '',
    preferredDate: '',
    preferredTime: '',
    topic: '',
    message: '',
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.facilitatorName) {
      newErrors.facilitatorName = 'Please select a facilitator'
    }
    return Object.keys(newErrors).length > 0 ? newErrors : null
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrors({})

    const validationErrors = validateForm()
    if (validationErrors) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)

    try {
      const response = await api.post(API_ENDPOINTS.PROGRAMS.BOOKING_REQUESTS(programSlug), formData)

      if (response.data.success) {
        setSubmitted(true)
        onSuccess && onSuccess(response.data.data)
        
        // Reset form after a delay
        setTimeout(() => {
          setFormData({
            facilitatorName: '',
            preferredDate: '',
            preferredTime: '',
            topic: '',
            message: '',
          })
          setSubmitted(false)
          onClose()
        }, 2000)
      }
    } catch (error) {
      console.error('Error submitting booking request:', error)
      setErrors({
        general: error.response?.data?.message || 'Failed to submit request. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      facilitatorName: '',
      preferredDate: '',
      preferredTime: '',
      topic: '',
      message: '',
    })
    setErrors({})
    setSubmitted(false)
    onClose()
  }

  const selectedFacilitator = facilitators.find(f => f.name === formData.facilitatorName)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <AnimatePresence mode="wait">
                {submitted ? (
                  /* Success screen */
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="p-8 text-center"
                  >
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                      className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                    >
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h2>
                    <p className="text-gray-600">
                      We&apos;ve received your interest in a 1-on-1 session with <strong>{formData.facilitatorName}</strong>. 
                      Our team will reach out to you soon.
                    </p>
                  </motion.div>
                ) : (
                  /* Form screen */
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-6 md:p-8"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">
                          Book a 1-on-1 Session
                        </h2>
                        <p className="text-gray-600 text-sm">
                          Express your interest in a personalized session with one of our facilitators
                        </p>
                      </div>
                      <button
                        onClick={handleClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Facilitator Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <User className="w-4 h-4 inline mr-1" />
                          Select Facilitator <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="facilitatorName"
                          value={formData.facilitatorName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-200 transition-all appearance-none bg-white ${
                            errors.facilitatorName 
                              ? 'border-red-300 focus:border-red-500' 
                              : 'border-gray-300 focus:border-purple-500'
                          }`}
                        >
                          <option value="">Choose a facilitator...</option>
                          {facilitators.map((facilitator) => (
                            <option key={facilitator.name} value={facilitator.name}>
                              {facilitator.name} - {facilitator.facilitatorTitle}
                            </option>
                          ))}
                        </select>
                        {errors.facilitatorName && (
                          <p className="text-red-600 text-sm mt-1">{errors.facilitatorName}</p>
                        )}
                        {selectedFacilitator && (
                          <div className="mt-2 p-3 bg-purple-50 rounded-lg">
                            <p className="text-sm text-purple-700">
                              <strong>Facilitor for Session {selectedFacilitator.session}:</strong> {selectedFacilitator.title}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Preferred Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          Preferred Date <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="date"
                          name="preferredDate"
                          value={formData.preferredDate}
                          onChange={handleInputChange}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                        />
                      </div>

                      {/* Preferred Time */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Preferred Time <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <select
                          name="preferredTime"
                          value={formData.preferredTime}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all appearance-none bg-white"
                        >
                          <option value="">Select a time slot...</option>
                          <option value="morning">Morning (6AM - 12PM)</option>
                          <option value="afternoon">Afternoon (12PM - 5PM)</option>
                          <option value="evening">Evening (5PM - 9PM)</option>
                          <option value="flexible">Flexible / Any time</option>
                        </select>
                      </div>

                      {/* Topic */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageSquare className="w-4 h-4 inline mr-1" />
                          What would you like to focus on? <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="text"
                          name="topic"
                          value={formData.topic}
                          onChange={handleInputChange}
                          placeholder="e.g., Stress management, Yoga practice, Nutrition guidance..."
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all"
                        />
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          📝 Additional Message <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Any specific questions or things you'd like the facilitator to know..."
                          rows={3}
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all resize-none"
                        />
                      </div>

                      {/* Error message */}
                      {errors.general && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <p className="text-red-600 text-sm">{errors.general}</p>
                        </div>
                      )}

                      {/* Buttons */}
                      <div className="flex gap-4 pt-2">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/25"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center gap-2">
                              <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                              />
                              Submitting...
                            </span>
                          ) : (
                            'Submit Request'
                          )}
                        </button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default BookOneOnOneModal

